import * as React from "react";
import { graphql } from "gatsby";
import styled from "styled-components";
import PageContainer from "../pageContainer";
import { GatsbyImage } from "gatsby-plugin-image";
import { SectionTitle } from "../typography";
import { useLocalization } from "gatsby-theme-i18n";
import { when } from "../../styles/theme";
import { useInView } from "react-intersection-observer";

const translations = {
  en: {
    address: "Address",
    email: "Email",
    phone: "Phone",
  },
  zh: {
    address: "地址",
    email: "郵箱",
    phone: "電話",
  },
};

export const fragment = graphql`
  fragment InvestorContactsContent on ContentfulPageSectionInvestorContacts {
    name
    title
    contactLogo1 {
      description
      gatsbyImageData(layout: CONSTRAINED, width: 108, height: 108)
    }
    contactTitle1
    contactName1
    contactAddress1 {
      childMarkdownRemark {
        html
      }
    }
    contactAddressLink1
    contactEmail1
    contactLogo2 {
      description
      gatsbyImageData(layout: CONSTRAINED, width: 108, height: 108)
    }
    contactTitle2
    contactEmail2
    contactPhone2
    internal {
      type
    }
  }
`;

const InvestorContactsSection = styled(PageContainer)`
  margin-bottom: 10rem;
  margin-top: 10rem;
`;

const ContentContainer = styled.div`
  margin-top: 6.4rem;

  ${when("md")} {
    display: flex;
    column-gap: ${(props) => props.theme.gutterWidth.md}rem;
  }

  ${when("lg")} {
    column-gap: ${(props) => props.theme.gutterWidth.lg}rem;
  }

  ${when("xl")} {
    column-gap: ${(props) => props.theme.gutterWidth.xl}rem;
  }

  ${when("xxl")} {
    column-gap: ${(props) => props.theme.gutterWidth.xxl}rem;
  }
`;

const Content = styled.div`
  flex: 1;
  margin-top: 4.8rem;

  :first-child {
    margin-top: 0;
  }

  ${when("md")} {
    margin-top: 0;
  }
`;

const ImageWrapper = styled.div`
  text-align: center;

  ${when("md")} {
    text-align: left;
  }
`;

const Icon = styled(GatsbyImage)`
  width: 7rem;
  height: 7rem;

  ${when("md")} {
    width: 10.8rem;
    height: 10.8rem;
  }
`;

const Info = styled.div`
  text-align: center;
  margin-top: 1rem;

  font-size: ${(props) => (props.isEn ? "1.4rem" : "1.6rem")};
  font-weight: 500;

  ${when("md")} {
    text-align: left;
    font-size: ${(props) => (props.isEn ? "1.6rem" : "2rem")};
  }
`;

const ContactTitle = styled.div`
  font-size: 2.4rem;
  //line-height: 1.5;
  color: ${(props) => props.theme.colors.red};
  ${when("md")} {
    font-size: 3.2rem;
  }
`;

const ContactName = styled.div``;

const ContactAddress = styled.div`
  span {
    p {
      display: inline;
    }
  }
`;

const ContactEmail = styled.a`
  //text-transform: uppercase;
  display: block;
`;

const ContactPhone = styled.a`
  display: block;
`;

const InfoInnerContainer = styled.div`
  margin-top: 1rem;
`;

const InvestorContacts = ({ data, setCurrentSection, index }) => {
  const { ref } = useInView({
    onChange: (inView) => {
      if (inView && setCurrentSection) {
        setCurrentSection(index);
      }
    },
    rootMargin: "-30% 0% -30% 0%",
  });

  const {
    title,
    contactLogo1,
    contactTitle1,
    contactName1,
    contactEmail1,
    contactAddress1,
    contactAddressLink1,
    contactPhone1,
    contactLogo2,
    contactTitle2,
    contactName2,
    contactEmail2,
    contactAddress2,
    contactPhone2,
  } = data;

  const { locale } = useLocalization();

  const translation = translations[locale];

  return (
    <InvestorContactsSection
      as={"section"}
      hasSideBar={true}
      ref={ref}
      id={`section-${index}`}
    >
      <SectionTitle>{title}</SectionTitle>
      <ContentContainer>
        <Content>
          {!!contactLogo1 && (
            <ImageWrapper>
              <Icon
                image={contactLogo1.gatsbyImageData}
                alt={contactLogo1.description}
              />
            </ImageWrapper>
          )}
          <Info isEn={locale === "en"}>
            {!!contactTitle1 && <ContactTitle>{contactTitle1}</ContactTitle>}
            <InfoInnerContainer>
              {!!contactName1 && <ContactName>{contactName1}</ContactName>}
              {!!contactAddress1 && (
                <ContactAddress
                  as="a"
                  href={contactAddressLink1}
                  target="_blank"
                >
                  {translation["address"]}:{" "}
                  <span
                    dangerouslySetInnerHTML={{
                      __html: contactAddress1.childMarkdownRemark.html,
                    }}
                  />
                </ContactAddress>
              )}
              {!!contactEmail1 && (
                <ContactEmail href={`mailto:${contactEmail1}`}>
                  {translation["email"]}: {contactEmail1}
                </ContactEmail>
              )}
              {!!contactPhone1 && (
                <ContactPhone href={`tel:${contactPhone1}`}>
                  {translation["phone"]}: {contactPhone1}
                </ContactPhone>
              )}
            </InfoInnerContainer>
          </Info>
        </Content>
        <Content>
          {contactLogo2 && (
            <ImageWrapper>
              <Icon
                image={contactLogo2.gatsbyImageData}
                alt={contactLogo2.description}
              />
            </ImageWrapper>
          )}
          <Info isEn={locale === "en"}>
            {!!contactTitle2 && <ContactTitle>{contactTitle2}</ContactTitle>}
            <InfoInnerContainer>
              {!!contactName2 && <ContactName>{contactName2}</ContactName>}
              {!!contactAddress2 && (
                <ContactAddress>
                  {translation["address"]}:{" "}
                  <span
                    dangerouslySetInnerHTML={{
                      __html: contactAddress2.childMarkdownRemark.html,
                    }}
                  />
                </ContactAddress>
              )}
              {!!contactEmail2 && (
                <ContactEmail href={`mailto:${contactEmail2}`}>
                  {translation["email"]}: {contactEmail2}
                </ContactEmail>
              )}
              {!!contactPhone2 && (
                <ContactPhone href={`tel:${contactPhone2}`}>
                  {translation["phone"]}: {contactPhone2}
                </ContactPhone>
              )}
            </InfoInnerContainer>
          </Info>
        </Content>
      </ContentContainer>
    </InvestorContactsSection>
  );
};

export const investorContactsName = "ContentfulPageSectionInvestorContacts";

export default InvestorContacts;
